// pages/Admin/Transactions.tsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  confirmDeposit,
  rejectDeposit,
  processWithdrawal,
  rejectWithdrawal,
  getAllDeposits,
  getAllWithdrawals,
  clearError,
  setCurrentPage,
} from "../../store/slices/adminSlice";
import Header from "../../components/Layout/Header";
import BottomNav from "../../components/Layout/BottomNav";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import Modal from "../../components/UI/Modal";
import Drawer from "../../components/UI/Drawer";
import Alert from "../../components/UI/Alert";
import globalStyle from "../../styles/globalStyle";
import { formatXRP } from "../../utils/formatters";

import {
  ArrowUpIcon,
  ArrowDownIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  UserIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  ArrowTopRightOnSquareIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ApiTransaction {
  id: number;
  user_id: number;
  type:
    | "deposit"
    | "withdrawal"
    | "trade_earning"
    | "airdrop"
    | "commission"
    | "bot_earning"
    | "referral_earning";
  amount: number;
  status: "pending" | "completed" | "rejected";
  tx_id?: string;
  tx_hash?: string;
  destination_address?: string;
  memo?: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    username: string;
    email: string;
  };
}

interface DisplayTransaction {
  id: number;
  user_id: number;
  type: "deposit" | "withdrawal";
  amount: number;
  status: "pending" | "completed" | "rejected";
  tx_id?: string;
  tx_hash?: string;
  destination_address?: string;
  memo?: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    username: string;
    email: string;
  };
}

// ─── Helper: is this transaction an admin credit? ─────────────────────────────
// Reads the metadata flag set by the backend on confirmed admin-credit deposits.
const isAdminCreditTx = (tx: DisplayTransaction): boolean =>
  tx.type === "deposit" && tx.metadata?.is_admin_credit === true;

// ─── XRPL Explorer ────────────────────────────────────────────────────────────
const XRPL_EXPLORER_BASE = "https://livenet.xrpl.org/transactions/";

// ─── Admin-Credit Badge ───────────────────────────────────────────────────────
// Small pill shown ONLY to admins so they can tell at a glance which deposits
// bypassed referral commissions.
const AdminCreditBadge: React.FC<{ size?: "xs" | "sm" }> = ({
  size = "xs",
}) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full font-semibold tracking-wide border border-amber-500/40 bg-amber-500/10 text-amber-400 ${
      size === "xs" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-xs"
    }`}
    title="Admin-issued credit — no referral commissions triggered"
  >
    <ShieldCheckIcon className="w-3 h-3" />
    Admin Credit
  </span>
);

// ─── Component ────────────────────────────────────────────────────────────────

const AdminTransactions: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    isLoading,
    error,
    actionLoading,
    allDeposits,
    allWithdrawals,
    totalPages,
    currentPage,
  } = useSelector((state: RootState) => state.admin);

  const [activeTab, setActiveTab] = useState<"deposits" | "withdrawals">(
    "deposits",
  );
  const [selectedTransaction, setSelectedTransaction] =
    useState<DisplayTransaction | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showActionModal, setShowActionModal] = useState<{
    open: boolean;
    type: "confirm" | "reject";
    transaction: DisplayTransaction | null;
  }>({ open: false, type: "confirm", transaction: null });
  const [showFilters, setShowFilters] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [committedSearch, setCommittedSearch] = useState("");

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: "",
    end: "",
  });
  const [txHash, setTxHash] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  // ── NEW: admin-credit toggle state ─────────────────────────────────────────
  const [isAdminCredit, setIsAdminCredit] = useState(false);

  const [localPage, setLocalPage] = useState(1);
  const [validationErrors, setValidationErrors] = useState<{
    txHash?: string;
    rejectionReason?: string;
  }>({});

  useEffect(() => {
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, localPage, statusFilter, committedSearch]);

  useEffect(() => {
    if (!showActionModal.open) {
      setTxHash("");
      setAdminNotes("");
      setRejectionReason("");
      setIsAdminCredit(false); // ← reset toggle on modal close
      setValidationErrors({});
      dispatch(clearError());
    }
  }, [showActionModal.open, dispatch]);

  useEffect(() => {
    dispatch(setCurrentPage(localPage));
  }, [localPage, dispatch]);

  const loadTransactions = () => {
    const filters = {
      page: localPage,
      limit: 20,
      status: statusFilter !== "all" ? statusFilter : undefined,
      search: committedSearch.trim() || undefined,
    };
    if (activeTab === "deposits") {
      dispatch(getAllDeposits(filters));
    } else {
      dispatch(getAllWithdrawals(filters));
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setCommittedSearch(searchInput);
      setLocalPage(1);
    }
  };

  const handleSearchSubmit = () => {
    setCommittedSearch(searchInput);
    setLocalPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setCommittedSearch("");
    setLocalPage(1);
  };

  const getCurrentTransactions = (): DisplayTransaction[] => {
    const transactions =
      activeTab === "deposits" ? allDeposits : allWithdrawals;
    return (transactions as ApiTransaction[])
      .filter((tx) => tx.type === "deposit" || tx.type === "withdrawal")
      .map((tx) => ({ ...tx, type: tx.type as "deposit" | "withdrawal" }));
  };

  // ─── Explorer URL helpers ─────────────────────────────────────────────────

  const getExplorerUrl = (hashOrId: string) =>
    `${XRPL_EXPLORER_BASE}${hashOrId}`;

  const getExplorerTarget = (
    tx: DisplayTransaction,
  ): { value: string; label: string } | null => {
    if (tx.tx_hash) {
      return { value: tx.tx_hash, label: "View on Explorer" };
    }
    if (tx.type === "deposit" && tx.tx_id && tx.tx_id !== "N/A") {
      return { value: tx.tx_id, label: "Verify TX ID on Explorer" };
    }
    return null;
  };

  // ─── Validation ───────────────────────────────────────────────────────────

  const validateConfirm = () => {
    const errors: typeof validationErrors = {};
    if (!txHash.trim()) {
      errors.txHash = "Transaction hash is required";
    } else if (txHash.trim().length < 10) {
      errors.txHash = "Transaction hash appears to be invalid";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateRejection = () => {
    const errors: typeof validationErrors = {};
    if (!rejectionReason.trim()) {
      errors.rejectionReason = "Rejection reason is required";
    } else if (rejectionReason.trim().length < 10) {
      errors.rejectionReason =
        "Rejection reason must be at least 10 characters";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ─── Action handlers ──────────────────────────────────────────────────────

  const handleConfirm = async () => {
    if (!showActionModal.transaction || !validateConfirm()) return;
    if (activeTab === "deposits") {
      const result = await dispatch(
        confirmDeposit({
          transactionId: showActionModal.transaction.id,
          txHash: txHash.trim(),
          adminNotes: adminNotes.trim() || undefined,
          isAdminCredit, // ← NEW: pass the toggle value
        }),
      );
      if (confirmDeposit.fulfilled.match(result)) {
        handleCloseActionModal();
        loadTransactions();
      }
    } else {
      const result = await dispatch(
        processWithdrawal({
          transactionId: showActionModal.transaction.id,
          txHash: txHash.trim(),
          adminNotes: adminNotes.trim() || undefined,
        }),
      );
      if (processWithdrawal.fulfilled.match(result)) {
        handleCloseActionModal();
        loadTransactions();
      }
    }
  };

  const handleReject = async () => {
    if (!showActionModal.transaction || !validateRejection()) return;
    if (activeTab === "deposits") {
      const result = await dispatch(
        rejectDeposit({
          transactionId: showActionModal.transaction.id,
          rejectionReason: rejectionReason.trim(),
        }),
      );
      if (rejectDeposit.fulfilled.match(result)) {
        handleCloseActionModal();
        loadTransactions();
      }
    } else {
      const result = await dispatch(
        rejectWithdrawal({
          transactionId: showActionModal.transaction.id,
          rejectionReason: rejectionReason.trim(),
        }),
      );
      if (rejectWithdrawal.fulfilled.match(result)) {
        handleCloseActionModal();
        loadTransactions();
      }
    }
  };

  const handleCloseActionModal = () => {
    setShowActionModal({ open: false, type: "confirm", transaction: null });
    setTxHash("");
    setAdminNotes("");
    setRejectionReason("");
    setIsAdminCredit(false); // ← reset
    setValidationErrors({});
  };

  // ─── Formatters ───────────────────────────────────────────────────────────

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatAmount = (amount: number | string) => {
    const n = typeof amount === "string" ? parseFloat(amount) : amount;
    if (Number.isNaN(n) || n === undefined || n === null) return "0.00";
    return formatXRP(n);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  const calculateStats = () => {
    const transactions = getCurrentTransactions();
    const total = transactions.reduce((s, t) => s + t.amount, 0);
    const completed = transactions
      .filter((t) => t.status === "completed")
      .reduce((s, t) => s + t.amount, 0);
    const pending = transactions
      .filter((t) => t.status === "pending")
      .reduce((s, t) => s + t.amount, 0);
    const rejected = transactions
      .filter((t) => t.status === "rejected")
      .reduce((s, t) => s + t.amount, 0);
    return {
      totalCount: transactions.length,
      totalAmount: total,
      completedCount: transactions.filter((t) => t.status === "completed")
        .length,
      completedAmount: completed,
      pendingCount: transactions.filter((t) => t.status === "pending").length,
      pendingAmount: pending,
      rejectedCount: transactions.filter((t) => t.status === "rejected").length,
      rejectedAmount: rejected,
    };
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return `${globalStyle.badge} ${globalStyle.badgeSuccess}`;
      case "pending":
        return `${globalStyle.badge} ${globalStyle.badgeWarning}`;
      case "rejected":
        return `${globalStyle.badge} ${globalStyle.badgeDanger}`;
      default:
        return `${globalStyle.badge} ${globalStyle.badgeInfo}`;
    }
  };

  const handleStatusFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setStatusFilter(e.target.value);
    setLocalPage(1);
  };

  const handlePageChange = (newPage: number) => setLocalPage(newPage);

  const switchTab = (tab: "deposits" | "withdrawals") => {
    setActiveTab(tab);
    setLocalPage(1);
    setSearchInput("");
    setCommittedSearch("");
    setStatusFilter("all");
    setDateRange({ start: "", end: "" });
  };

  const stats = calculateStats();
  const currentTransactions = getCurrentTransactions();

  // ─── Shared Explorer Link component ───────────────────────────────────────

  const ExplorerLink: React.FC<{
    target: { value: string; label: string };
    size?: "sm" | "md";
  }> = ({ target, size = "sm" }) => (
    <a
      href={getExplorerUrl(target.value)}
      target="_blank"
      rel="noopener noreferrer"
      title={target.label}
      className={`inline-flex items-center gap-1 rounded font-medium border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition-colors whitespace-nowrap ${
        size === "sm" ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-xs"
      }`}
    >
      <ArrowTopRightOnSquareIcon className="w-3 h-3" />
      {target.label}
    </a>
  );

  // ─── Admin-Credit Toggle (shown only in confirm-deposit modal) ─────────────

  const AdminCreditToggle: React.FC = () => (
    <div
      className={`rounded-lg p-4 border transition-colors ${
        isAdminCredit
          ? "bg-amber-950/40 border-amber-500/50"
          : "bg-gray-800/60 border-gray-700"
      }`}
    >
      <label className="flex items-start gap-3 cursor-pointer select-none">
        {/* Toggle switch */}
        <div className="relative mt-0.5 flex-shrink-0">
          <input
            type="checkbox"
            className="sr-only"
            checked={isAdminCredit}
            onChange={(e) => setIsAdminCredit(e.target.checked)}
          />
          <div
            onClick={() => setIsAdminCredit((v) => !v)}
            className={`w-10 h-6 rounded-full transition-colors cursor-pointer ${
              isAdminCredit ? "bg-amber-500" : "bg-gray-600"
            }`}
          />
          <div
            onClick={() => setIsAdminCredit((v) => !v)}
            className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform cursor-pointer ${
              isAdminCredit ? "translate-x-5" : "translate-x-1"
            }`}
          />
        </div>

        {/* Label + explanation */}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <ShieldCheckIcon
              className={`w-4 h-4 ${isAdminCredit ? "text-amber-400" : "text-gray-500"}`}
            />
            <span
              className={`text-sm font-semibold ${isAdminCredit ? "text-amber-300" : "text-gray-300"}`}
            >
              Mark as Admin Credit
            </span>
            {isAdminCredit && <AdminCreditBadge />}
          </div>
          <p className="text-xs mt-1 text-gray-400 leading-relaxed">
            Enable this if <strong className="text-gray-300">you</strong> (the
            admin) are crediting this deposit on behalf of the platform — e.g. a
            bonus, compensation, or internal transfer. When enabled:
          </p>
          <ul className="text-xs mt-1.5 text-gray-400 space-y-0.5 list-none">
            <li className="flex items-center gap-1.5">
              <span
                className={isAdminCredit ? "text-amber-400" : "text-gray-600"}
              >
                ✓
              </span>
              User's XRP balance is credited normally
            </li>
            <li className="flex items-center gap-1.5">
              <span
                className={isAdminCredit ? "text-red-400" : "text-gray-600"}
              >
                ✗
              </span>
              No referral commissions paid to uplines
            </li>
            <li className="flex items-center gap-1.5">
              <span
                className={isAdminCredit ? "text-red-400" : "text-gray-600"}
              >
                ✗
              </span>
              Does NOT count as the user's "first deposit"
            </li>
            <li className="flex items-center gap-1.5">
              <span
                className={isAdminCredit ? "text-red-400" : "text-gray-600"}
              >
                ✗
              </span>
              Excluded from team volume calculations
            </li>
          </ul>
          {isAdminCredit && (
            <p className="text-xs mt-2 text-amber-400 font-medium">
              ⚠️ Only you (admin) can see this label. The user sees it as a
              normal deposit in their history.
            </p>
          )}
        </div>
      </label>
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className={globalStyle.container}>
      <Header />

      <main className="page-content">
        {/* Page header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className={globalStyle.h3}>Transaction Management</h1>
            <p className={globalStyle.body2 + " text-gray-400"}>
              View and manage all deposits and withdrawals
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex space-x-3">
            <Button
              variant="tertiary"
              onClick={loadTransactions}
              loading={isLoading}
            >
              <ArrowPathIcon className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <p className={globalStyle.caption + " text-gray-400"}>
                  Total {activeTab === "deposits" ? "Deposits" : "Withdrawals"}
                </p>
                <h3 className={globalStyle.h3}>{stats.totalCount}</h3>
                <p className={`${globalStyle.caption} mt-1 text-gray-400`}>
                  {formatAmount(stats.totalAmount)} XRP
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-900 flex items-center justify-center">
                <CurrencyDollarIcon className="w-6 h-6 text-blue-300" />
              </div>
            </div>
          </Card>
          <Card padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <p className={globalStyle.caption + " text-gray-400"}>
                  Completed
                </p>
                <h3 className={globalStyle.h3}>{stats.completedCount}</h3>
                <p className={`${globalStyle.caption} mt-1 text-gray-400`}>
                  {formatAmount(stats.completedAmount)} XRP
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-green-900 flex items-center justify-center">
                <CheckCircleIcon className="w-6 h-6 text-green-300" />
              </div>
            </div>
          </Card>
          <Card padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <p className={globalStyle.caption + " text-gray-400"}>
                  Pending
                </p>
                <h3 className={globalStyle.h3}>{stats.pendingCount}</h3>
                <p className={`${globalStyle.caption} mt-1 text-gray-400`}>
                  {formatAmount(stats.pendingAmount)} XRP
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-yellow-900 flex items-center justify-center">
                <ClockIcon className="w-6 h-6 text-yellow-300" />
              </div>
            </div>
          </Card>
          <Card padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <p className={globalStyle.caption + " text-gray-400"}>
                  Rejected
                </p>
                <h3 className={globalStyle.h3}>{stats.rejectedCount}</h3>
                <p className={`${globalStyle.caption} mt-1 text-gray-400`}>
                  {formatAmount(stats.rejectedAmount)} XRP
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-red-900 flex items-center justify-center">
                <XCircleIcon className="w-6 h-6 text-red-300" />
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <div className="mt-6">
          <div className="flex space-x-1 border-b border-gray-800">
            <button
              className={`px-6 py-3 font-medium ${globalStyle.transition} ${
                activeTab === "deposits"
                  ? "text-primary border-b-2 border-primary"
                  : "text-gray-400 hover:text-gray-300"
              }`}
              onClick={() => switchTab("deposits")}
            >
              <div className="flex items-center">
                <ArrowUpIcon className="w-4 h-4 mr-2" />
                Deposits
              </div>
            </button>
            <button
              className={`px-6 py-3 font-medium ${globalStyle.transition} ${
                activeTab === "withdrawals"
                  ? "text-primary border-b-2 border-primary"
                  : "text-gray-400 hover:text-gray-300"
              }`}
              onClick={() => switchTab("withdrawals")}
            >
              <div className="flex items-center">
                <ArrowDownIcon className="w-4 h-4 mr-2" />
                Withdrawals
              </div>
            </button>
          </div>
        </div>

        {/* Search & Filters */}
        <Card className="mt-6" padding="lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder={`Search by username, email${
                    activeTab === "deposits" ? ", TX ID" : ", address"
                  }, or hash…`}
                  className={`${globalStyle.input} pl-10`}
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                    title="Clear search"
                  >
                    <XCircleIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Button onClick={handleSearchSubmit} loading={isLoading}>
                Search
              </Button>
            </div>
            <div className="flex space-x-3 shrink-0">
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-400">Status:</label>
                <select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  className={`${globalStyle.input} py-1 px-3 text-sm`}
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <Button variant="tertiary" onClick={() => setShowFilters(true)}>
                <FunnelIcon className="w-4 h-4 mr-2" />
                Date Filter
              </Button>
            </div>
          </div>
          {committedSearch && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-gray-400">
                Searching for:{" "}
                <span className="text-white font-medium">
                  "{committedSearch}"
                </span>
              </span>
              <button
                onClick={handleClearSearch}
                className="text-xs text-primary hover:underline"
              >
                Clear
              </button>
            </div>
          )}
        </Card>

        {/* Transactions Table */}
        <Card className="mt-6" padding="lg">
          {error && (
            <Alert
              type="error"
              message={error}
              className="mb-6"
              onClose={() => dispatch(clearError())}
            />
          )}

          {isLoading ? (
            <div className="text-center py-12">
              <svg
                className={`${globalStyle.loadingSpinner} mx-auto`}
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className={`${globalStyle.body2} mt-4 text-gray-400`}>
                Loading transactions...
              </p>
            </div>
          ) : currentTransactions.length === 0 ? (
            <div className="text-center py-12">
              {activeTab === "deposits" ? (
                <ArrowUpIcon className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              ) : (
                <ArrowDownIcon className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              )}
              <h3 className={globalStyle.h5}>No Transactions Found</h3>
              <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
                {committedSearch ||
                statusFilter !== "all" ||
                dateRange.start ||
                dateRange.end
                  ? "Try adjusting your search or filters."
                  : `No ${activeTab} found.`}
              </p>
              {committedSearch && (
                <Button
                  variant="tertiary"
                  className="mt-4"
                  onClick={handleClearSearch}
                >
                  Clear Search
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className={globalStyle.table}>
                  <thead className={globalStyle.tableHead}>
                    <tr>
                      <th className={globalStyle.tableHeadCell}>User</th>
                      <th className={globalStyle.tableHeadCell}>Amount</th>
                      <th className={globalStyle.tableHeadCell}>Status</th>
                      <th className={globalStyle.tableHeadCell}>Details</th>
                      <th className={globalStyle.tableHeadCell}>Date</th>
                      <th className={globalStyle.tableHeadCell}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={globalStyle.tableBody}>
                    {currentTransactions.map((transaction) => {
                      const explorerTarget = getExplorerTarget(transaction);
                      const adminCredit = isAdminCreditTx(transaction);
                      return (
                        <tr
                          key={transaction.id}
                          className={globalStyle.tableRowHover}
                        >
                          {/* User */}
                          <td className={globalStyle.tableCell}>
                            <div className="flex items-center">
                              <div className="w-8 h-8 rounded-full bg-primary-900 flex items-center justify-center mr-3">
                                <UserIcon className="w-4 h-4 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium text-white">
                                  {transaction.user?.username || "N/A"}
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* Amount */}
                          <td className={globalStyle.tableCell}>
                            <div className="space-y-1">
                              <div
                                className={`font-medium ${
                                  activeTab === "deposits"
                                    ? "text-green-500"
                                    : "text-red-500"
                                }`}
                              >
                                {activeTab === "deposits" ? "+" : "-"}
                                {formatAmount(transaction.amount)} XRP
                              </div>
                              {/* ── Admin-credit badge in the amount cell ── */}
                              {adminCredit && <AdminCreditBadge />}
                            </div>
                          </td>

                          {/* Status */}
                          <td className={globalStyle.tableCell}>
                            <span
                              className={getStatusBadge(transaction.status)}
                            >
                              {transaction.status.charAt(0).toUpperCase() +
                                transaction.status.slice(1)}
                            </span>
                          </td>

                          {/* Details */}
                          <td className={globalStyle.tableCell}>
                            <div className="max-w-[220px] space-y-1">
                              {activeTab === "deposits" ? (
                                <>
                                  {transaction.tx_id &&
                                    transaction.tx_id !== "N/A" && (
                                      <div>
                                        <p className="text-xs text-gray-500">
                                          TX ID (user ref):
                                        </p>
                                        <p className="text-xs truncate text-gray-300 font-mono">
                                          {transaction.tx_id}
                                        </p>
                                      </div>
                                    )}
                                  {transaction.tx_hash ? (
                                    <div>
                                      <p className="text-xs text-gray-500">
                                        Blockchain hash:
                                      </p>
                                      <p className="text-xs truncate text-gray-300 font-mono">
                                        {transaction.tx_hash}
                                      </p>
                                    </div>
                                  ) : (
                                    transaction.status === "pending" && (
                                      <p className="text-xs text-yellow-400">
                                        Awaiting confirmation
                                      </p>
                                    )
                                  )}
                                </>
                              ) : (
                                <>
                                  <p className="text-sm truncate text-gray-300">
                                    To: {transaction.destination_address}
                                  </p>
                                  {transaction.memo && (
                                    <p
                                      className={`${globalStyle.caption} text-gray-400`}
                                    >
                                      Memo: {transaction.memo}
                                    </p>
                                  )}
                                  {transaction.tx_hash && (
                                    <p
                                      className={`${globalStyle.caption} truncate text-gray-400`}
                                    >
                                      Hash: {transaction.tx_hash}
                                    </p>
                                  )}
                                  {transaction.status === "pending" &&
                                    !transaction.tx_hash && (
                                      <p className="text-xs text-yellow-400">
                                        Pending processing
                                      </p>
                                    )}
                                </>
                              )}
                            </div>
                          </td>

                          {/* Date */}
                          <td
                            className={globalStyle.tableCell + " text-gray-300"}
                          >
                            {formatDate(transaction.created_at)}
                          </td>

                          {/* Actions */}
                          <td className={globalStyle.tableCell}>
                            <div className="flex items-center flex-wrap gap-1.5">
                              <Button
                                size="sm"
                                variant="tertiary"
                                onClick={() => {
                                  setSelectedTransaction(transaction);
                                  setShowDetails(true);
                                }}
                              >
                                <EyeIcon className="w-4 h-4" />
                              </Button>

                              {explorerTarget && (
                                <ExplorerLink target={explorerTarget} />
                              )}

                              {transaction.status === "pending" && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="success"
                                    onClick={() =>
                                      setShowActionModal({
                                        open: true,
                                        type: "confirm",
                                        transaction,
                                      })
                                    }
                                  >
                                    <CheckCircleIcon className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="danger"
                                    onClick={() =>
                                      setShowActionModal({
                                        open: true,
                                        type: "reject",
                                        transaction,
                                      })
                                    }
                                  >
                                    <XCircleIcon className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-800 pt-4 mt-4">
                  <div className="text-sm text-gray-400">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="tertiary"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="tertiary"
                      size="sm"
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </main>

      <BottomNav />

      {/* ── Transaction Details Modal ──────────────────────────────────────────── */}
      <Modal
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        title="Transaction Details"
        size="lg"
      >
        {selectedTransaction && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div
                  className={`w-12 h-12 rounded-lg flex items-center justify-center mr-3 ${
                    activeTab === "deposits" ? "bg-green-900" : "bg-red-900"
                  }`}
                >
                  {activeTab === "deposits" ? (
                    <ArrowUpIcon className="w-6 h-6 text-green-300" />
                  ) : (
                    <ArrowDownIcon className="w-6 h-6 text-red-300" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={globalStyle.h5}>
                      {activeTab === "deposits" ? "Deposit" : "Withdrawal"} #
                      {selectedTransaction.id}
                    </h3>
                    {/* ── Admin-credit badge in the details modal header ── */}
                    {isAdminCreditTx(selectedTransaction) && (
                      <AdminCreditBadge size="sm" />
                    )}
                  </div>
                  <p className={globalStyle.caption + " text-gray-400"}>
                    Created: {formatDate(selectedTransaction.created_at)}
                  </p>
                  <span className={getStatusBadge(selectedTransaction.status)}>
                    {selectedTransaction.status.charAt(0).toUpperCase() +
                      selectedTransaction.status.slice(1)}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p
                  className={`${globalStyle.h4} ${
                    activeTab === "deposits" ? "text-green-500" : "text-red-500"
                  }`}
                >
                  {activeTab === "deposits" ? "+" : "-"}
                  {formatAmount(selectedTransaction.amount)} XRP
                </p>
              </div>
            </div>

            {/* Admin-credit info banner (only visible in detail modal, admin-only) */}
            {isAdminCreditTx(selectedTransaction) && (
              <div className="rounded-lg p-4 border border-amber-500/40 bg-amber-950/30">
                <div className="flex items-start gap-3">
                  <ShieldCheckIcon className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-300">
                      Admin-Issued Credit
                    </p>
                    <p className="text-xs text-amber-400/80 mt-1 leading-relaxed">
                      This deposit was manually credited by an admin. No
                      referral commissions were paid to the user's uplines, and
                      it does not count as the user's first deposit for
                      commission purposes. It is also excluded from team volume
                      calculations. The user sees it as a normal deposit in
                      their transaction history.
                    </p>
                    {selectedTransaction.metadata?.confirmed_by && (
                      <p className="text-xs text-amber-500 mt-1">
                        Credited by Admin #
                        {selectedTransaction.metadata.confirmed_by}
                        {selectedTransaction.metadata.confirmed_at &&
                          ` on ${formatDate(selectedTransaction.metadata.confirmed_at)}`}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* User + Timeline */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>User</h4>
                <div className="flex items-center mt-1">
                  <UserIcon className="w-4 h-4 text-gray-500 mr-2" />
                  <div>
                    <p className="font-medium text-white">
                      {selectedTransaction.user?.username || "N/A"}
                    </p>

                    <p className={globalStyle.caption + " text-gray-400"}>
                      User ID: {selectedTransaction.user_id}
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>Timeline</h4>
                <div className="mt-1 space-y-1">
                  <p className="text-sm text-gray-300">
                    Created: {formatDate(selectedTransaction.created_at)}
                  </p>
                  <p className="text-sm text-gray-300">
                    Updated: {formatDate(selectedTransaction.updated_at)}
                  </p>
                </div>
              </div>
            </div>

            {/* Deposit details */}
            {activeTab === "deposits" ? (
              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>
                  Deposit Details
                </h4>
                <div className="mt-2 space-y-3">
                  <div className="bg-gray-800 p-3 rounded-lg">
                    <p className="text-xs text-gray-400 mb-2">
                      Transaction Reference (TX ID — submitted by user)
                    </p>
                    <div className="flex items-start justify-between flex-wrap gap-2">
                      <code className="text-sm text-white font-mono break-all flex-1">
                        {selectedTransaction.tx_id || "N/A"}
                      </code>
                      {selectedTransaction.tx_id &&
                        selectedTransaction.tx_id !== "N/A" && (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() =>
                                copyToClipboard(selectedTransaction.tx_id || "")
                              }
                              className="p-1 hover:bg-gray-700 rounded text-white"
                              title="Copy TX ID"
                            >
                              <DocumentDuplicateIcon className="w-4 h-4" />
                            </button>
                            {!selectedTransaction.tx_hash && (
                              <ExplorerLink
                                target={{
                                  value: selectedTransaction.tx_id!,
                                  label: "Verify TX ID on Explorer",
                                }}
                                size="md"
                              />
                            )}
                          </div>
                        )}
                    </div>
                  </div>

                  {selectedTransaction.tx_hash ? (
                    <div className="bg-gray-800 p-3 rounded-lg">
                      <p className="text-xs text-gray-400 mb-2">
                        Blockchain Transaction Hash (confirmed)
                      </p>
                      <div className="flex items-start justify-between flex-wrap gap-2">
                        <code className="text-sm text-white font-mono break-all flex-1">
                          {selectedTransaction.tx_hash}
                        </code>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() =>
                              copyToClipboard(selectedTransaction.tx_hash || "")
                            }
                            className="p-1 hover:bg-gray-700 rounded text-white"
                            title="Copy hash"
                          >
                            <DocumentDuplicateIcon className="w-4 h-4" />
                          </button>
                          <ExplorerLink
                            target={{
                              value: selectedTransaction.tx_hash,
                              label: "View on Explorer",
                            }}
                            size="md"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-yellow-900 border border-yellow-800 rounded-lg p-3">
                      <p className="text-sm text-yellow-200">
                        ⏳ No blockchain hash yet — this deposit is{" "}
                        <strong>pending confirmation</strong>. Click{" "}
                        <strong>Verify TX ID on Explorer</strong> above to look
                        up the user's submitted transaction on the XRPL ledger
                        and verify the amount before approving.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>
                  Withdrawal Details
                </h4>
                <div className="mt-2 space-y-3">
                  <div className="bg-gray-800 p-3 rounded-lg">
                    <p className="text-xs text-gray-400 mb-1">
                      Destination Address
                    </p>
                    <div className="flex items-center justify-between">
                      <code className="text-sm text-white font-mono break-all">
                        {selectedTransaction.destination_address || "N/A"}
                      </code>
                      {selectedTransaction.destination_address && (
                        <button
                          onClick={() =>
                            copyToClipboard(
                              selectedTransaction.destination_address || "",
                            )
                          }
                          className="p-1 hover:bg-gray-700 rounded text-white ml-2"
                        >
                          <DocumentDuplicateIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {selectedTransaction.memo && (
                    <div className="bg-gray-800 p-3 rounded-lg">
                      <p className="text-xs text-gray-400 mb-1">
                        Memo / Destination Tag
                      </p>
                      <div className="flex items-center justify-between">
                        <code className="text-sm text-white font-mono break-all">
                          {selectedTransaction.memo}
                        </code>
                        <button
                          onClick={() =>
                            copyToClipboard(selectedTransaction.memo || "")
                          }
                          className="p-1 hover:bg-gray-700 rounded text-white ml-2"
                        >
                          <DocumentDuplicateIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedTransaction.tx_hash ? (
                    <div className="bg-gray-800 p-3 rounded-lg">
                      <p className="text-xs text-gray-400 mb-2">
                        Blockchain Transaction Hash (confirmed)
                      </p>
                      <div className="flex items-start justify-between flex-wrap gap-2">
                        <code className="text-sm text-white font-mono break-all flex-1">
                          {selectedTransaction.tx_hash}
                        </code>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() =>
                              copyToClipboard(selectedTransaction.tx_hash || "")
                            }
                            className="p-1 hover:bg-gray-700 rounded text-white"
                            title="Copy hash"
                          >
                            <DocumentDuplicateIcon className="w-4 h-4" />
                          </button>
                          <ExplorerLink
                            target={{
                              value: selectedTransaction.tx_hash,
                              label: "View on Explorer",
                            }}
                            size="md"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    selectedTransaction.status === "pending" && (
                      <div className="bg-yellow-900 border border-yellow-800 rounded-lg p-3">
                        <p className="text-sm text-yellow-200">
                          ⏳ This withdrawal is pending processing. Once you
                          send the XRP and enter the blockchain hash, it will
                          appear here.
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {/* Metadata */}
            {selectedTransaction.metadata &&
              Object.keys(selectedTransaction.metadata).length > 0 && (
                <div>
                  <h4 className={`${globalStyle.h6} text-gray-400`}>
                    Additional Metadata
                  </h4>
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <pre className="text-sm overflow-x-auto text-gray-300">
                      {JSON.stringify(selectedTransaction.metadata, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

            {/* Rejection banner */}
            {selectedTransaction.status === "rejected" &&
              selectedTransaction.metadata?.rejection_reason && (
                <div className="bg-red-900 border border-red-800 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-red-300 mb-2">
                    Rejection Reason
                  </h4>
                  <p className="text-sm text-red-200">
                    {selectedTransaction.metadata.rejection_reason}
                  </p>
                  {selectedTransaction.metadata.rejected_by && (
                    <p className="text-xs text-red-300 mt-2">
                      Rejected by: Admin #
                      {selectedTransaction.metadata.rejected_by} on{" "}
                      {formatDate(selectedTransaction.metadata.rejected_at)}
                    </p>
                  )}
                </div>
              )}

            {/* Confirmation banner */}
            {selectedTransaction.status === "completed" &&
              selectedTransaction.metadata?.confirmed_by && (
                <div className="bg-green-900 border border-green-800 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-green-300 mb-2">
                    Confirmation Details
                  </h4>
                  <p className="text-sm text-green-200">
                    Confirmed by: Admin #
                    {selectedTransaction.metadata.confirmed_by ||
                      selectedTransaction.metadata.processed_by}
                  </p>
                  <p className="text-xs text-green-300 mt-1">
                    Confirmed at:{" "}
                    {formatDate(
                      selectedTransaction.metadata.confirmed_at ||
                        selectedTransaction.metadata.processed_at,
                    )}
                  </p>
                  {selectedTransaction.metadata.admin_notes && (
                    <p className="text-sm text-green-200 mt-2">
                      Notes: {selectedTransaction.metadata.admin_notes}
                    </p>
                  )}
                  {selectedTransaction.tx_hash && (
                    <a
                      href={getExplorerUrl(selectedTransaction.tx_hash)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-green-300 hover:text-green-100 transition-colors"
                    >
                      <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                      Verify on XRPL Explorer
                    </a>
                  )}
                </div>
              )}

            {/* Footer */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-800">
              <Button variant="tertiary" onClick={() => setShowDetails(false)}>
                Close
              </Button>
              {selectedTransaction.status === "pending" && (
                <>
                  <Button
                    variant="success"
                    onClick={() => {
                      setShowActionModal({
                        open: true,
                        type: "confirm",
                        transaction: selectedTransaction,
                      });
                      setShowDetails(false);
                    }}
                  >
                    <CheckCircleIcon className="w-4 h-4 mr-2" />
                    Confirm
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      setShowActionModal({
                        open: true,
                        type: "reject",
                        transaction: selectedTransaction,
                      });
                      setShowDetails(false);
                    }}
                  >
                    <XCircleIcon className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* ── Action Modal ───────────────────────────────────────────────────────── */}
      <Modal
        isOpen={showActionModal.open}
        onClose={handleCloseActionModal}
        title={
          showActionModal.type === "confirm"
            ? activeTab === "deposits"
              ? "Confirm Deposit"
              : "Process Withdrawal"
            : activeTab === "deposits"
              ? "Reject Deposit"
              : "Reject Withdrawal"
        }
        size="md"
      >
        {showActionModal.transaction && (
          <div className="flex flex-col max-h-[75vh]">
            <div className="overflow-y-auto flex-1 space-y-4 pr-1">
              {error && (
                <Alert
                  type="error"
                  message={error}
                  className="mb-4"
                  onClose={() => dispatch(clearError())}
                />
              )}

              {/* Summary */}
              <div className="bg-gray-800 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">
                      {showActionModal.transaction.user?.username ||
                        "Unknown User"}
                    </p>
                    <p className={globalStyle.caption + " text-gray-400"}>
                      {formatAmount(showActionModal.transaction.amount)} XRP
                    </p>
                    {activeTab === "deposits" &&
                      showActionModal.transaction.tx_id &&
                      showActionModal.transaction.tx_id !== "N/A" && (
                        <div className="flex items-center gap-2 mt-2">
                          <p className={`${globalStyle.caption} text-gray-400`}>
                            TX ID: {showActionModal.transaction.tx_id}
                          </p>
                          <ExplorerLink
                            target={{
                              value: showActionModal.transaction.tx_id,
                              label: "Verify on Explorer",
                            }}
                          />
                        </div>
                      )}
                  </div>
                  <div className="text-right">
                    <p className={globalStyle.caption + " text-gray-400"}>
                      Requested
                    </p>
                    <p className="text-sm text-white">
                      {formatDate(showActionModal.transaction.created_at)}
                    </p>
                  </div>
                </div>
              </div>

              {showActionModal.type === "confirm" ? (
                <>
                  <div className={globalStyle.formGroup}>
                    <label className={globalStyle.label}>
                      Transaction Hash *
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={txHash}
                        onChange={(e) => {
                          setTxHash(e.target.value);
                          setValidationErrors({});
                        }}
                        className={`${globalStyle.input} flex-1 ${
                          validationErrors.txHash ? "border-red-500" : ""
                        }`}
                        placeholder="Enter blockchain transaction hash"
                      />
                      {txHash.trim().length >= 10 && (
                        <a
                          href={getExplorerUrl(txHash.trim())}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Preview on XRPL Explorer"
                          className="inline-flex items-center gap-1 px-3 py-2 rounded text-xs font-medium border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition-colors whitespace-nowrap"
                        >
                          <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                          Verify
                        </a>
                      )}
                    </div>
                    {validationErrors.txHash && (
                      <p className="text-red-500 text-sm mt-1">
                        {validationErrors.txHash}
                      </p>
                    )}
                    <p className={`${globalStyle.caption} mt-2 text-gray-400`}>
                      This will be recorded as the confirmed transaction on the
                      blockchain
                    </p>
                  </div>

                  <div className={globalStyle.formGroup}>
                    <label className={globalStyle.label}>
                      Admin Notes (Optional)
                    </label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      className={`${globalStyle.input} min-h-[80px] resize-none`}
                      placeholder="Add any notes or comments..."
                    />
                  </div>

                  {/* ── NEW: Admin-Credit Toggle — only shown for deposits ───────── */}
                  {activeTab === "deposits" && <AdminCreditToggle />}

                  <div className="bg-yellow-900 border border-yellow-800 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-yellow-300 mb-2 flex items-center">
                      <InformationCircleIcon className="w-4 h-4 mr-2" />
                      {activeTab === "deposits"
                        ? "Confirming Deposit"
                        : "Processing Withdrawal"}
                    </h4>
                    <ul className="text-sm text-yellow-200 space-y-1">
                      <li>
                        • Use <strong>Verify on Explorer</strong> (shown above)
                        to check the user's TX ID on the XRPL ledger before
                        confirming
                      </li>
                      <li>• Ensure the amount matches the user's request</li>
                      {activeTab === "deposits" &&
                        showActionModal.transaction.tx_id &&
                        showActionModal.transaction.tx_id !== "N/A" && (
                          <li>
                            • User's TX ID: {showActionModal.transaction.tx_id}
                          </li>
                        )}
                      {activeTab === "withdrawals" && (
                        <>
                          <li>• Verify the destination address is correct</li>
                          {showActionModal.transaction.memo && (
                            <li>
                              • Include memo/destination tag:{" "}
                              {showActionModal.transaction.memo}
                            </li>
                          )}
                        </>
                      )}
                      <li>• User will receive an email notification</li>
                      {activeTab === "deposits" && !isAdminCredit && (
                        <li>• User's balance will be credited immediately</li>
                      )}
                      {activeTab === "deposits" && isAdminCredit && (
                        <li className="text-amber-300 font-medium">
                          • ⚠️ Marked as Admin Credit — no referral commissions
                          will be paid
                        </li>
                      )}
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <div className={globalStyle.formGroup}>
                    <label className={globalStyle.label}>
                      Rejection Reason *
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => {
                        setRejectionReason(e.target.value);
                        setValidationErrors({});
                      }}
                      className={`${globalStyle.input} min-h-[100px] resize-none ${
                        validationErrors.rejectionReason ? "border-red-500" : ""
                      }`}
                      placeholder="Explain why this transaction is being rejected..."
                      required
                    />
                    {validationErrors.rejectionReason && (
                      <p className="text-red-500 text-sm mt-1">
                        {validationErrors.rejectionReason}
                      </p>
                    )}
                    <p className={`${globalStyle.caption} mt-2 text-gray-400`}>
                      Minimum 10 characters. This reason will be sent to the
                      user.
                    </p>
                  </div>

                  <div className="bg-red-900 border border-red-800 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-red-300 mb-2 flex items-center">
                      <InformationCircleIcon className="w-4 h-4 mr-2" />
                      {activeTab === "deposits"
                        ? "Rejecting Deposit"
                        : "Rejecting Withdrawal"}
                    </h4>
                    <ul className="text-sm text-red-200 space-y-1">
                      <li>• Provide a clear and professional reason</li>
                      <li>• User will receive an email notification</li>
                      {activeTab === "withdrawals" && (
                        <li>• User's balance will be refunded</li>
                      )}
                      <li>• This action cannot be undone</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
            {/* end scrollable area */}

            {/* ── Action buttons — always visible, pinned at bottom ── */}
            <div className="pt-4 mt-2 border-t border-gray-800 flex space-x-3">
              {showActionModal.type === "confirm" ? (
                <>
                  <Button
                    variant="tertiary"
                    onClick={handleCloseActionModal}
                    fullWidth
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirm}
                    loading={actionLoading}
                    disabled={!txHash.trim()}
                    fullWidth
                    className={
                      isAdminCredit
                        ? "!bg-amber-600 hover:!bg-amber-500 !border-amber-500"
                        : ""
                    }
                  >
                    {isAdminCredit
                      ? "Confirm as Admin Credit"
                      : activeTab === "deposits"
                        ? "Confirm Deposit"
                        : "Process Withdrawal"}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="tertiary"
                    onClick={handleCloseActionModal}
                    fullWidth
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleReject}
                    loading={actionLoading}
                    disabled={rejectionReason.trim().length < 10}
                    fullWidth
                  >
                    {activeTab === "deposits"
                      ? "Reject Deposit"
                      : "Reject Withdrawal"}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* ── Date Filters Drawer ────────────────────────────────────────────────── */}
      <Drawer
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        title="Date Range Filter"
        position="right"
        size="sm"
      >
        <div className="space-y-6">
          <div>
            <h4 className={`${globalStyle.h6} mb-3 text-white`}>
              Select Date Range
            </h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, start: e.target.value })
                  }
                  className={globalStyle.input}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  To Date
                </label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, end: e.target.value })
                  }
                  className={globalStyle.input}
                />
              </div>
            </div>
          </div>
          <Button
            onClick={() => {
              setLocalPage(1);
              setShowFilters(false);
              loadTransactions();
            }}
            fullWidth
          >
            Apply Date Filter
          </Button>
          <Button
            variant="tertiary"
            onClick={() => {
              setDateRange({ start: "", end: "" });
              setLocalPage(1);
              setShowFilters(false);
              loadTransactions();
            }}
            fullWidth
          >
            Clear Date Filter
          </Button>
        </div>
      </Drawer>
    </div>
  );
};

export default AdminTransactions;
